import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Ban, Download, FileText, Loader2, Plus, Save, Upload, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getApiErrorMessage } from "@/lib/api";
import { formatCnpj } from "@/lib/format";
import { formatDateTimeSP } from "@/lib/formatDate";
import { hasPermission } from "@/lib/types";
import { useApp } from "@/store/useApp";
import { useTabTitle } from "@/layouts/adminTabs/tabRuntime";
import {
  addTaxRegime,
  createBankAccount,
  createPixKey,
  deactivateBankAccount,
  deactivatePixKey,
  downloadDocument,
  extractCartaoCnpj,
  getEstablishment,
  listBankAccounts,
  listDocuments,
  listPixKeys,
  listTaxRegimes,
  succeedCnpj,
  updateBankAccount,
  updateEstablishmentProfile,
  updatePixKey,
  uploadDocument,
  type BankAccountRequest,
  type BankAccountResponse,
  type CartaoCnpjExtractionPreview,
  type DocumentResponse,
  type EstablishmentDetailResponse,
  type EstablishmentDocType,
  type EstablishmentProfileRequest,
  type PixKeyRequest,
  type PixKeyResponse,
  type PixKeyType,
  type TaxRegimeResponse,
} from "@/lib/adminEstablishmentsApi";
import {
  buildExtractionReview,
  buildProfileFromReview,
  type ExtractionReview,
} from "./establishmentExtraction";

// Permissões (o backend é a fonte de verdade; os gates abaixo são só UX).
const P_EDIT = "FINANCEIRO.ESTABELECIMENTO.EDITAR";
const P_CREATE = "FINANCEIRO.ESTABELECIMENTO.CRIAR";
const P_FIN_VIEW = "FINANCEIRO.ESTABELECIMENTO.FINANCEIRO_VISUALIZAR";
const P_FIN_MANAGE = "FINANCEIRO.ESTABELECIMENTO.FINANCEIRO_GERENCIAR";
const P_DOC_VIEW = "FINANCEIRO.ESTABELECIMENTO.DOCUMENTO_VISUALIZAR";
const P_DOC_MANAGE = "FINANCEIRO.ESTABELECIMENTO.DOCUMENTO_GERENCIAR";

const DOC_TYPE_LABEL: Record<EstablishmentDocType, string> = {
  CARTAO_CNPJ: "Cartão CNPJ",
  CONTRATO_SOCIAL: "Contrato social",
  ALTERACAO_CONTRATUAL: "Alteração contratual",
};

const PIX_TYPE_LABEL: Record<PixKeyType, string> = {
  CPF: "CPF",
  CNPJ: "CNPJ",
  EMAIL: "E-mail",
  TELEFONE: "Telefone",
  ALEATORIA: "Aleatória",
};

/** Regimes tributários aceitos (o backend persiste a string; mantemos o rótulo legível só na UI). */
const TAX_REGIME_OPTIONS: { value: string; label: string }[] = [
  { value: "SIMPLES_NACIONAL", label: "Simples Nacional" },
  { value: "LUCRO_PRESUMIDO", label: "Lucro Presumido" },
  { value: "LUCRO_REAL", label: "Lucro Real" },
  { value: "MEI", label: "MEI" },
  { value: "IMUNE_ISENTO", label: "Imune / Isento" },
];

const taxRegimeLabel = (value: string): string =>
  TAX_REGIME_OPTIONS.find((o) => o.value === value)?.label ?? value;

const inputCls = "border rounded px-2 py-1 text-sm text-foreground disabled:opacity-60";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
      <span>{label}</span>
      {children}
    </label>
  );
}

export default function AdminEstablishmentDetail() {
  const { id = "" } = useParams();
  const account = useApp((s) => s.account);

  const canEdit = hasPermission(account, P_EDIT);
  const canCreate = hasPermission(account, P_CREATE);
  const canFinView = hasPermission(account, P_FIN_VIEW);
  const canFinManage = hasPermission(account, P_FIN_MANAGE);
  const canDocView = hasPermission(account, P_DOC_VIEW);
  const canDocManage = hasPermission(account, P_DOC_MANAGE);

  const [detail, setDetail] = useState<EstablishmentDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Mostra o nome da empresa no rótulo da aba interna (em vez do UUID da rota).
  useTabTitle(detail?.name);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      setDetail(await getEstablishment(id));
    } catch (e) {
      setLoadError(getApiErrorMessage(e, "Falha ao carregar o estabelecimento"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground" role="status">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando…
      </div>
    );
  }
  if (loadError || !detail) {
    return (
      <div className="container max-w-4xl py-6 space-y-4">
        <Link to="/admin/estabelecimentos" className="inline-flex items-center gap-1 text-sm text-primary">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Link>
        <div role="alert" className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          {loadError ?? "Estabelecimento não encontrado."}
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-5xl py-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Link to="/admin/estabelecimentos" className="inline-flex items-center gap-1 text-xs text-primary">
            <ArrowLeft className="h-3 w-3" /> Estabelecimentos
          </Link>
          <h1 className="mt-1 text-xl font-semibold">{detail.name}</h1>
          <p className="text-sm text-muted-foreground">
            CNPJ {formatCnpj(detail.cnpj)} · {detail.active ? "ativo" : "inativo"}
          </p>
          {detail.predecessorId && (
            <p className="text-xs text-muted-foreground">
              Sucede o estabelecimento{" "}
              <Link
                to={`/admin/estabelecimentos/${encodeURIComponent(detail.predecessorId)}`}
                className="text-primary underline-offset-2 hover:underline"
              >
                anterior
              </Link>
              .
            </p>
          )}
        </div>
      </div>

      <Tabs defaultValue="identidade">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="identidade">Identidade</TabsTrigger>
          <TabsTrigger value="endereco">Endereço &amp; contatos</TabsTrigger>
          <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
          <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
          <TabsTrigger value="documentos">Documentos</TabsTrigger>
        </TabsList>

        <TabsContent value="identidade">
          <ProfileTab detail={detail} canEdit={canEdit} canCreate={canCreate} onSaved={load} />
        </TabsContent>
        <TabsContent value="endereco">
          <AddressTab detail={detail} canEdit={canEdit} onSaved={load} />
        </TabsContent>
        <TabsContent value="fiscal">
          <FiscalTab establishmentId={id} current={detail.currentTaxRegime} canEdit={canEdit} onChanged={load} />
        </TabsContent>
        <TabsContent value="financeiro">
          <FinanceTab
            establishmentId={id}
            canView={canFinView}
            canManage={canFinManage}
          />
        </TabsContent>
        <TabsContent value="documentos">
          <DocumentsTab
            detail={detail}
            canView={canDocView}
            canManage={canDocManage}
            canEdit={canEdit}
            onProfileApplied={load}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ------------------------------------------------------------------ Identidade

function ProfileTab({
  detail,
  canEdit,
  canCreate,
  onSaved,
}: {
  detail: EstablishmentDetailResponse;
  canEdit: boolean;
  canCreate: boolean;
  onSaved: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState(detail.name ?? "");
  const [tradeName, setTradeName] = useState(detail.tradeName ?? "");
  const [stateRegistration, setStateRegistration] = useState(detail.stateRegistration ?? "");
  const [municipalRegistration, setMunicipalRegistration] = useState(detail.municipalRegistration ?? "");
  const [busy, setBusy] = useState(false);
  const [succeedOpen, setSucceedOpen] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body: EstablishmentProfileRequest = {
        ...profileSnapshot(detail),
        name: name.trim() || detail.name,
        tradeName: tradeName.trim() || null,
        stateRegistration: stateRegistration.trim() || null,
        municipalRegistration: municipalRegistration.trim() || null,
      };
      await updateEstablishmentProfile(detail.id, body);
      toast.success("Identidade atualizada.");
      await onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao salvar"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-border p-4 space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="CNPJ">
          <input className={inputCls} value={formatCnpj(detail.cnpj)} readOnly aria-label="CNPJ" />
        </Field>
        <Field label="Razão social">
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} />
        </Field>
        <Field label="Nome fantasia">
          <input className={inputCls} value={tradeName} onChange={(e) => setTradeName(e.target.value)} disabled={!canEdit} />
        </Field>
        <Field label="Inscrição estadual">
          <input className={inputCls} value={stateRegistration} onChange={(e) => setStateRegistration(e.target.value)} disabled={!canEdit} />
        </Field>
        <Field label="Inscrição municipal">
          <input className={inputCls} value={municipalRegistration} onChange={(e) => setMunicipalRegistration(e.target.value)} disabled={!canEdit} />
        </Field>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Para trocar o CNPJ, crie um estabelecimento sucessor.
      </p>
      <div className="flex flex-wrap gap-2">
        {canEdit && (
          <Button size="sm" onClick={() => void save()} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar identidade
          </Button>
        )}
        {canCreate && (
          <Button size="sm" variant="outline" onClick={() => setSucceedOpen(true)}>
            Trocar CNPJ (criar sucessor)
          </Button>
        )}
      </div>

      <SucceedCnpjDialog
        open={succeedOpen}
        onOpenChange={setSucceedOpen}
        establishmentId={detail.id}
        defaultName={detail.name}
        onCreated={(est) => {
          setSucceedOpen(false);
          toast.success("Estabelecimento sucessor criado.");
          navigate(`/admin/estabelecimentos/${encodeURIComponent(est.id)}`);
        }}
      />
    </section>
  );
}

function SucceedCnpjDialog({
  open,
  onOpenChange,
  establishmentId,
  defaultName,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  establishmentId: string;
  defaultName: string;
  onCreated: (est: { id: string }) => void;
}) {
  const [cnpj, setCnpj] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setCnpj(""); setName(""); }
  }, [open]);

  const submit = async () => {
    const digits = cnpj.replace(/\D/g, "");
    if (digits.length !== 14) { toast.error("Informe um CNPJ com 14 dígitos."); return; }
    setBusy(true);
    try {
      const est = await succeedCnpj(establishmentId, digits, name.trim() || undefined);
      onCreated(est);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao criar sucessor"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Trocar CNPJ (criar sucessor)</DialogTitle>
          <DialogDescription>
            A troca de CNPJ não é uma edição: cria um novo estabelecimento sucessor, preservando o atual no histórico.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Novo CNPJ">
            <input className={inputCls} value={cnpj} onChange={(e) => setCnpj(e.target.value)} placeholder="00.000.000/0000-00" />
          </Field>
          <Field label="Nome / razão social (opcional)">
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder={defaultName} />
          </Field>
        </div>
        <DialogFooter>
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
          <Button size="sm" onClick={() => void submit()} disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Criar sucessor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ Endereço & contatos

function AddressTab({
  detail,
  canEdit,
  onSaved,
}: {
  detail: EstablishmentDetailResponse;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState(() => ({
    email: detail.email ?? "",
    phone: detail.phone ?? "",
    whatsapp: detail.whatsapp ?? "",
    website: detail.website ?? "",
    addressZipCode: detail.addressZipCode ?? "",
    addressStreet: detail.addressStreet ?? "",
    addressNumber: detail.addressNumber ?? "",
    addressComplement: detail.addressComplement ?? "",
    addressDistrict: detail.addressDistrict ?? "",
    addressCity: detail.addressCity ?? "",
    addressState: detail.addressState ?? "",
    addressCountry: detail.addressCountry ?? "",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }));
  const trimOrNull = (v: string) => (v.trim() ? v.trim() : null);

  const save = async () => {
    setBusy(true);
    try {
      const body: EstablishmentProfileRequest = {
        ...profileSnapshot(detail),
        email: trimOrNull(f.email),
        phone: trimOrNull(f.phone),
        whatsapp: trimOrNull(f.whatsapp),
        website: trimOrNull(f.website),
        addressZipCode: trimOrNull(f.addressZipCode),
        addressStreet: trimOrNull(f.addressStreet),
        addressNumber: trimOrNull(f.addressNumber),
        addressComplement: trimOrNull(f.addressComplement),
        addressDistrict: trimOrNull(f.addressDistrict),
        addressCity: trimOrNull(f.addressCity),
        addressState: trimOrNull(f.addressState),
        addressCountry: trimOrNull(f.addressCountry),
      };
      await updateEstablishmentProfile(detail.id, body);
      toast.success("Endereço e contatos atualizados.");
      await onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao salvar"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-border p-4 space-y-4">
      <div className="text-sm font-semibold">Contatos</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="E-mail"><input className={inputCls} value={f.email} onChange={set("email")} disabled={!canEdit} /></Field>
        <Field label="Telefone"><input className={inputCls} value={f.phone} onChange={set("phone")} disabled={!canEdit} /></Field>
        <Field label="WhatsApp"><input className={inputCls} value={f.whatsapp} onChange={set("whatsapp")} disabled={!canEdit} /></Field>
        <Field label="Website"><input className={inputCls} value={f.website} onChange={set("website")} disabled={!canEdit} /></Field>
      </div>
      <div className="text-sm font-semibold">Endereço</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="CEP"><input className={inputCls} value={f.addressZipCode} onChange={set("addressZipCode")} disabled={!canEdit} /></Field>
        <Field label="Logradouro"><input className={inputCls} value={f.addressStreet} onChange={set("addressStreet")} disabled={!canEdit} /></Field>
        <Field label="Número"><input className={inputCls} value={f.addressNumber} onChange={set("addressNumber")} disabled={!canEdit} /></Field>
        <Field label="Complemento"><input className={inputCls} value={f.addressComplement} onChange={set("addressComplement")} disabled={!canEdit} /></Field>
        <Field label="Bairro"><input className={inputCls} value={f.addressDistrict} onChange={set("addressDistrict")} disabled={!canEdit} /></Field>
        <Field label="Município"><input className={inputCls} value={f.addressCity} onChange={set("addressCity")} disabled={!canEdit} /></Field>
        <Field label="UF"><input className={inputCls} value={f.addressState} onChange={set("addressState")} disabled={!canEdit} /></Field>
        <Field label="País"><input className={inputCls} value={f.addressCountry} onChange={set("addressCountry")} disabled={!canEdit} /></Field>
      </div>
      {canEdit && (
        <Button size="sm" onClick={() => void save()} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar endereço e contatos
        </Button>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ Fiscal (regime tributário)

function FiscalTab({
  establishmentId,
  current,
  canEdit,
  onChanged,
}: {
  establishmentId: string;
  current: TaxRegimeResponse | null;
  canEdit: boolean;
  onChanged: () => Promise<void>;
}) {
  const [history, setHistory] = useState<TaxRegimeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [regime, setRegime] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setHistory(await listTaxRegimes(establishmentId));
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao carregar o histórico de regimes"));
    } finally {
      setLoading(false);
    }
  }, [establishmentId]);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    if (!regime.trim()) { toast.error("Informe o regime."); return; }
    if (!effectiveFrom) { toast.error("Informe a data de início de vigência."); return; }
    setBusy(true);
    try {
      await addTaxRegime(establishmentId, { regime: regime.trim(), effectiveFrom, note: note.trim() || null });
      toast.success("Regime tributário registrado.");
      setRegime(""); setEffectiveFrom(""); setNote("");
      await load();
      await onChanged();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao registrar o regime"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-border p-4 space-y-4">
      <div className="rounded-md border border-border bg-muted/20 p-3 text-sm">
        <span className="text-muted-foreground">Regime vigente: </span>
        {current ? (
          <span className="font-medium">
            {taxRegimeLabel(current.regime)} (desde {current.effectiveFrom})
          </span>
        ) : (
          <span className="text-muted-foreground">nenhum registrado</span>
        )}
      </div>

      {canEdit && (
        <div className="space-y-2">
          <div className="text-sm font-semibold">Registrar novo regime</div>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Regime">
              <select className={inputCls} value={regime} onChange={(e) => setRegime(e.target.value)} aria-label="Regime tributário">
                <option value="">selecione…</option>
                {TAX_REGIME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Vigência a partir de">
              <input type="date" className={inputCls} value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
            </Field>
            <Field label="Observação (opcional)">
              <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <Button size="sm" onClick={() => void add()} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Registrar
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          Histórico {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        {history.length === 0 ? (
          <div className="text-xs text-muted-foreground">Nenhum regime registrado.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="p-1">regime</th>
                  <th className="p-1">de</th>
                  <th className="p-1">até</th>
                  <th className="p-1">observação</th>
                  <th className="p-1">registrado por</th>
                  <th className="p-1">em</th>
                </tr>
              </thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="p-1">{taxRegimeLabel(r.regime)}</td>
                    <td className="p-1">{r.effectiveFrom}</td>
                    <td className="p-1">{r.effectiveTo ?? "—"}</td>
                    <td className="p-1">{r.note ?? "—"}</td>
                    <td className="p-1">{r.createdBy ?? "—"}</td>
                    <td className="p-1">{formatDateTimeSP(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ Financeiro (contas + Pix, registro puro)

function FinanceTab({
  establishmentId,
  canView,
  canManage,
}: {
  establishmentId: string;
  canView: boolean;
  canManage: boolean;
}) {
  if (!canView) {
    return (
      <section className="rounded-xl border border-border p-4">
        <p className="text-sm text-muted-foreground">
          Você não tem permissão para visualizar os dados financeiros deste estabelecimento.
        </p>
      </section>
    );
  }
  return (
    <div className="space-y-4">
      <BankAccountsPanel establishmentId={establishmentId} canManage={canManage} />
      <PixKeysPanel establishmentId={establishmentId} canManage={canManage} />
    </div>
  );
}

const EMPTY_BANK: BankAccountRequest = {
  bankCode: "", bankName: "", agency: "", accountNumber: "", accountType: "",
  holderName: "", holderDocument: "", note: "",
};

function BankAccountsPanel({ establishmentId, canManage }: { establishmentId: string; canManage: boolean }) {
  const [list, setList] = useState<BankAccountResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<BankAccountResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<BankAccountRequest>(EMPTY_BANK);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setList(await listBankAccounts(establishmentId));
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao carregar contas bancárias"));
    } finally {
      setLoading(false);
    }
  }, [establishmentId]);

  useEffect(() => { void load(); }, [load]);

  const openNew = () => { setEditing(null); setDraft(EMPTY_BANK); setOpen(true); };
  const openEdit = (acc: BankAccountResponse) => {
    setEditing(acc);
    setDraft({
      bankCode: acc.bankCode ?? "", bankName: acc.bankName ?? "", agency: acc.agency ?? "",
      accountNumber: acc.accountNumber ?? "", accountType: acc.accountType ?? "",
      holderName: acc.holderName ?? "", holderDocument: acc.holderDocument ?? "", note: acc.note ?? "",
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) await updateBankAccount(establishmentId, editing.id, draft);
      else await createBankAccount(establishmentId, draft);
      toast.success(editing ? "Conta atualizada." : "Conta cadastrada.");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao salvar a conta"));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (acc: BankAccountResponse) => {
    setBusy(true);
    try {
      await deactivateBankAccount(establishmentId, acc.id);
      toast.success("Conta desativada.");
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao desativar"));
    } finally {
      setBusy(false);
    }
  };

  const setD = (k: keyof BankAccountRequest) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <section className="rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          Contas bancárias {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        {canManage && (
          <Button size="sm" variant="outline" onClick={openNew}>
            <Plus className="h-4 w-4" /> Nova conta
          </Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Apenas cadastro de dados bancários do próprio estabelecimento. Não emite nem processa cobranças.
      </p>
      {list.length === 0 ? (
        <div className="text-xs text-muted-foreground">Nenhuma conta cadastrada.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="p-1">banco</th>
                <th className="p-1">agência</th>
                <th className="p-1">conta</th>
                <th className="p-1">tipo</th>
                <th className="p-1">titular</th>
                <th className="p-1">situação</th>
                <th className="p-1"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((acc) => (
                <tr key={acc.id} className="border-t border-border">
                  <td className="p-1">{[acc.bankCode, acc.bankName].filter(Boolean).join(" ") || "—"}</td>
                  <td className="p-1">{acc.agency ?? "—"}</td>
                  <td className="p-1">{acc.accountNumber ?? "—"}</td>
                  <td className="p-1">{acc.accountType ?? "—"}</td>
                  <td className="p-1">{acc.holderName ?? "—"}</td>
                  <td className="p-1">{acc.active ? "ativa" : "inativa"}</td>
                  <td className="p-1">
                    {canManage && acc.active && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => openEdit(acc)}>Editar</Button>
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void deactivate(acc)}>
                          <Ban className="h-3 w-3" /> Desativar
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar conta bancária" : "Nova conta bancária"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Código do banco"><input className={inputCls} value={draft.bankCode ?? ""} onChange={setD("bankCode")} /></Field>
            <Field label="Nome do banco"><input className={inputCls} value={draft.bankName ?? ""} onChange={setD("bankName")} /></Field>
            <Field label="Agência"><input className={inputCls} value={draft.agency ?? ""} onChange={setD("agency")} /></Field>
            <Field label="Conta"><input className={inputCls} value={draft.accountNumber ?? ""} onChange={setD("accountNumber")} /></Field>
            <Field label="Tipo"><input className={inputCls} value={draft.accountType ?? ""} onChange={setD("accountType")} placeholder="CORRENTE / POUPANCA" /></Field>
            <Field label="Titular"><input className={inputCls} value={draft.holderName ?? ""} onChange={setD("holderName")} /></Field>
            <Field label="Documento do titular"><input className={inputCls} value={draft.holderDocument ?? ""} onChange={setD("holderDocument")} /></Field>
            <Field label="Observação"><input className={inputCls} value={draft.note ?? ""} onChange={setD("note")} /></Field>
          </div>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button>
            <Button size="sm" onClick={() => void save()} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function PixKeysPanel({ establishmentId, canManage }: { establishmentId: string; canManage: boolean }) {
  const [list, setList] = useState<PixKeyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<PixKeyResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PixKeyRequest>({ keyType: "CNPJ", keyValue: "", description: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setList(await listPixKeys(establishmentId));
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao carregar chaves Pix"));
    } finally {
      setLoading(false);
    }
  }, [establishmentId]);

  useEffect(() => { void load(); }, [load]);

  const openNew = () => { setEditing(null); setDraft({ keyType: "CNPJ", keyValue: "", description: "" }); setOpen(true); };
  const openEdit = (pix: PixKeyResponse) => {
    setEditing(pix);
    setDraft({ keyType: pix.keyType, keyValue: pix.keyValue, description: pix.description ?? "" });
    setOpen(true);
  };

  const save = async () => {
    if (!draft.keyValue.trim()) { toast.error("Informe a chave."); return; }
    setBusy(true);
    try {
      const body: PixKeyRequest = {
        keyType: draft.keyType,
        keyValue: draft.keyValue.trim(),
        description: draft.description?.trim() || null,
      };
      if (editing) await updatePixKey(establishmentId, editing.id, body);
      else await createPixKey(establishmentId, body);
      toast.success(editing ? "Chave atualizada." : "Chave cadastrada.");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao salvar a chave"));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (pix: PixKeyResponse) => {
    setBusy(true);
    try {
      await deactivatePixKey(establishmentId, pix.id);
      toast.success("Chave desativada.");
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao desativar"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          Chaves Pix {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        {canManage && (
          <Button size="sm" variant="outline" onClick={openNew}>
            <Plus className="h-4 w-4" /> Nova chave
          </Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Apenas registro das chaves Pix do próprio estabelecimento. Não cria cobranças nem QR Codes de pagamento.
      </p>
      {list.length === 0 ? (
        <div className="text-xs text-muted-foreground">Nenhuma chave cadastrada.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="p-1">tipo</th>
                <th className="p-1">chave</th>
                <th className="p-1">descrição</th>
                <th className="p-1">situação</th>
                <th className="p-1"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((pix) => (
                <tr key={pix.id} className="border-t border-border">
                  <td className="p-1">{PIX_TYPE_LABEL[pix.keyType]}</td>
                  <td className="p-1">{pix.keyValue}</td>
                  <td className="p-1">{pix.description ?? "—"}</td>
                  <td className="p-1">{pix.active ? "ativa" : "inativa"}</td>
                  <td className="p-1">
                    {canManage && pix.active && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => openEdit(pix)}>Editar</Button>
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void deactivate(pix)}>
                          <Ban className="h-3 w-3" /> Desativar
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar chave Pix" : "Nova chave Pix"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Tipo">
              <select
                className={inputCls}
                value={draft.keyType}
                onChange={(e) => setDraft((prev) => ({ ...prev, keyType: e.target.value as PixKeyType }))}
              >
                {(Object.keys(PIX_TYPE_LABEL) as PixKeyType[]).map((t) => (
                  <option key={t} value={t}>{PIX_TYPE_LABEL[t]}</option>
                ))}
              </select>
            </Field>
            <Field label="Chave">
              <input className={inputCls} value={draft.keyValue} onChange={(e) => setDraft((prev) => ({ ...prev, keyValue: e.target.value }))} />
            </Field>
            <Field label="Descrição (opcional)">
              <input className={inputCls} value={draft.description ?? ""} onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))} />
            </Field>
          </div>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button>
            <Button size="sm" onClick={() => void save()} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// ------------------------------------------------------------------ Documentos + extração assistida

function DocumentsTab({
  detail,
  canView,
  canManage,
  canEdit,
  onProfileApplied,
}: {
  detail: EstablishmentDetailResponse;
  canView: boolean;
  canManage: boolean;
  canEdit: boolean;
  onProfileApplied: () => Promise<void>;
}) {
  const [docs, setDocs] = useState<DocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [docType, setDocType] = useState<EstablishmentDocType>("CARTAO_CNPJ");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [replaceId, setReplaceId] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Extração assistida
  const [extractOpen, setExtractOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDocs(await listDocuments(detail.id));
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao carregar documentos"));
    } finally {
      setLoading(false);
    }
  }, [detail.id]);

  useEffect(() => { if (canView) void load(); else setLoading(false); }, [canView, load]);

  if (!canView) {
    return (
      <section className="rounded-xl border border-border p-4">
        <p className="text-sm text-muted-foreground">
          Você não tem permissão para visualizar os documentos deste estabelecimento.
        </p>
      </section>
    );
  }

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Selecione um arquivo."); return; }
    setBusy(true);
    try {
      await uploadDocument(detail.id, {
        file,
        docType,
        title: title.trim() || undefined,
        note: note.trim() || undefined,
        replaceDocumentId: replaceId || undefined,
      });
      toast.success("Documento enviado.");
      setTitle(""); setNote(""); setReplaceId("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao enviar o documento"));
    } finally {
      setBusy(false);
    }
  };

  const download = async (doc: DocumentResponse) => {
    try {
      await downloadDocument(detail.id, doc.id, doc.fileName ?? `documento-${doc.id}`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao baixar o documento"));
    }
  };

  // Só versões vigentes (active) listadas primeiro; as substituídas ficam logo abaixo.
  const activeDocs = docs.filter((d) => d.active);
  const supersededDocs = docs.filter((d) => !d.active);

  return (
    <div className="space-y-4">
      {canManage && (
        <section className="rounded-xl border border-border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">Enviar documento</div>
            <Button size="sm" variant="outline" onClick={() => setExtractOpen(true)}>
              <Wand2 className="h-4 w-4" /> Preencher com cartão CNPJ
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Tipo">
              <select className={inputCls} value={docType} onChange={(e) => setDocType(e.target.value as EstablishmentDocType)}>
                {(Object.keys(DOC_TYPE_LABEL) as EstablishmentDocType[]).map((t) => (
                  <option key={t} value={t}>{DOC_TYPE_LABEL[t]}</option>
                ))}
              </select>
            </Field>
            <Field label="Título (opcional)"><input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
            <Field label="Observação (opcional)"><input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
            <Field label="Substituir versão (opcional)">
              <select className={inputCls} value={replaceId} onChange={(e) => setReplaceId(e.target.value)}>
                <option value="">nova (não substitui)</option>
                {activeDocs.map((d) => (
                  <option key={d.id} value={d.id}>{DOC_TYPE_LABEL[d.docType]} · v{d.version} · {d.fileName ?? d.id}</option>
                ))}
              </select>
            </Field>
            <Field label="Arquivo">
              <input ref={fileRef} type="file" className="text-sm" aria-label="Arquivo do documento" />
            </Field>
            <Button size="sm" onClick={() => void upload()} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Enviar
            </Button>
          </div>
        </section>
      )}

      <section className="rounded-xl border border-border p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          Documentos {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        {docs.length === 0 ? (
          <div className="text-xs text-muted-foreground">Nenhum documento cadastrado.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="p-1">tipo</th>
                  <th className="p-1">título / arquivo</th>
                  <th className="p-1">versão</th>
                  <th className="p-1">situação</th>
                  <th className="p-1">enviado por</th>
                  <th className="p-1">em</th>
                  <th className="p-1"></th>
                </tr>
              </thead>
              <tbody>
                {[...activeDocs, ...supersededDocs].map((d) => (
                  <tr key={d.id} className="border-t border-border">
                    <td className="p-1">{DOC_TYPE_LABEL[d.docType]}</td>
                    <td className="p-1">
                      <span className="inline-flex items-center gap-1">
                        <FileText className="h-3 w-3 text-muted-foreground" />
                        {d.title || d.fileName || d.id}
                      </span>
                    </td>
                    <td className="p-1 tabular-nums">v{d.version}</td>
                    <td className="p-1">{d.active ? "vigente" : "substituído"}</td>
                    <td className="p-1">{d.uploadedBy ?? "—"}</td>
                    <td className="p-1">{formatDateTimeSP(d.uploadedAt)}</td>
                    <td className="p-1">
                      <Button size="sm" variant="ghost" onClick={() => void download(d)}>
                        <Download className="h-3 w-3" /> Baixar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {canManage && (
        <CartaoCnpjExtractionDialog
          open={extractOpen}
          onOpenChange={setExtractOpen}
          detail={detail}
          canApply={canEdit}
          onApplied={async () => { setExtractOpen(false); await onProfileApplied(); }}
        />
      )}
    </div>
  );
}

function CartaoCnpjExtractionDialog({
  open,
  onOpenChange,
  detail,
  canApply,
  onApplied,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  detail: EstablishmentDetailResponse;
  canApply: boolean;
  onApplied: () => Promise<void>;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<CartaoCnpjExtractionPreview | null>(null);
  const [review, setReview] = useState<ExtractionReview | null>(null);
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const [applyCnaes, setApplyCnaes] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (open) {
      setPreview(null); setReview(null); setConfirmed(new Set()); setApplyCnaes(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open]);

  const extract = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Selecione o PDF do cartão CNPJ."); return; }
    setExtracting(true);
    try {
      const p = await extractCartaoCnpj(detail.id, file);
      setPreview(p);
      if (p.textExtractable) {
        const r = buildExtractionReview(detail, p);
        setReview(r);
        // Pré-marca só os campos que diferem (o usuário vê o diff e confirma).
        setConfirmed(new Set(r.fields.filter((f) => f.differs).map((f) => String(f.key))));
        setApplyCnaes(r.cnaesDiffer);
      } else {
        setReview(null);
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao extrair o cartão CNPJ"));
    } finally {
      setExtracting(false);
    }
  };

  const toggle = (key: string) => {
    setConfirmed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const apply = async () => {
    if (!review) return;
    if (confirmed.size === 0 && !applyCnaes) { toast.error("Selecione ao menos um campo para aplicar."); return; }
    setApplying(true);
    try {
      const body = buildProfileFromReview(detail, review, confirmed, applyCnaes);
      await updateEstablishmentProfile(detail.id, body);
      toast.success("Dados aplicados ao cadastro.");
      await onApplied();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao aplicar os dados"));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preencher com cartão CNPJ</DialogTitle>
          <DialogDescription>
            Envie o PDF do cartão CNPJ para extrair os dados. Nada é gravado até você revisar e confirmar quais
            campos aplicar — nenhum valor preenchido é sobrescrito sem a sua confirmação.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3">
          <Field label="Arquivo (PDF)">
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="text-sm" aria-label="Cartão CNPJ (PDF)" />
          </Field>
          <Button size="sm" onClick={() => void extract()} disabled={extracting}>
            {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Extrair
          </Button>
        </div>

        {preview && !preview.textExtractable && (
          <div role="alert" className="rounded-md border border-warning/50 bg-warning/10 p-3 text-sm">
            PDF sem texto — cadastre manualmente.
          </div>
        )}

        {preview?.textExtractable && review && (
          <div className="space-y-4">
            {review.fields.length === 0 && !review.cnaes.length ? (
              <p className="text-sm text-muted-foreground">Nenhum campo utilizável foi extraído.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-muted-foreground">
                    <tr>
                      <th className="p-1">aplicar</th>
                      <th className="p-1">campo</th>
                      <th className="p-1">atual</th>
                      <th className="p-1">extraído</th>
                    </tr>
                  </thead>
                  <tbody>
                    {review.fields.map((f) => (
                      <tr key={String(f.key)} className="border-t border-border">
                        <td className="p-1">
                          <Checkbox
                            checked={confirmed.has(String(f.key))}
                            onCheckedChange={() => toggle(String(f.key))}
                            disabled={!canApply}
                            aria-label={`Aplicar ${f.label}`}
                          />
                        </td>
                        <td className="p-1">{f.label}</td>
                        <td className={`p-1 ${f.differs ? "text-muted-foreground line-through" : ""}`}>
                          {f.current || "—"}
                        </td>
                        <td className={`p-1 ${f.differs ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                          {f.extracted}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {review.cnaes.length > 0 && (
              <div className="rounded-md border border-border p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={applyCnaes}
                    onCheckedChange={(v) => setApplyCnaes(v === true)}
                    disabled={!canApply}
                    aria-label="Aplicar CNAEs extraídos"
                  />
                  Substituir a lista de CNAEs pela extraída
                  {review.cnaesDiffer && <span className="text-[11px] text-warning">(difere do atual)</span>}
                </label>
                <ul className="text-xs text-muted-foreground">
                  {review.cnaes.map((c) => (
                    <li key={`${c.code}-${c.primary}`}>
                      {c.primary ? "Principal" : "Secundário"}: {c.code} — {c.description}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!canApply && (
              <p className="text-[11px] text-warning">
                Você não tem permissão para editar o perfil ({P_EDIT}); a aplicação está desabilitada.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)} disabled={applying}>Fechar</Button>
          {preview?.textExtractable && review && canApply && (
            <Button size="sm" onClick={() => void apply()} disabled={applying}>
              {applying && <Loader2 className="h-4 w-4 animate-spin" />} Aplicar selecionados
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Snapshot do perfil atual como corpo-base do PUT /profile (preserva campos não tocados pelo formulário). */
function profileSnapshot(d: EstablishmentDetailResponse): EstablishmentProfileRequest {
  return {
    name: d.name,
    tradeName: d.tradeName,
    stateRegistration: d.stateRegistration,
    municipalRegistration: d.municipalRegistration,
    email: d.email,
    phone: d.phone,
    whatsapp: d.whatsapp,
    website: d.website,
    addressZipCode: d.addressZipCode,
    addressStreet: d.addressStreet,
    addressNumber: d.addressNumber,
    addressComplement: d.addressComplement,
    addressDistrict: d.addressDistrict,
    addressCity: d.addressCity,
    addressState: d.addressState,
    addressCountry: d.addressCountry,
    cnaes: d.cnaes,
  };
}
